<template>
  <div class="invoice-container">
    <div class="invoice-actions">
      <button @click="approve" class="btn btn-approve" :disabled="hasBalanceWarning || saving"
        title="Approve the invoice.">
        Approve Invoice
      </button>
      <button @click="approveAndNext" class="btn btn-approve" :disabled="hasBalanceWarning || saving"
        title="Approve the invoice and move to next.">
        Approve & Next
      </button>
      <!--
      <button @click="reject" class="btn btn-reject" :disabled="saving" title="Reject the invoice.">
        Reject Invoice
      </button>
     -->
      <button class="btn btn-save" :disabled="saving" title="Re-assign the invoice to another user.">
        Re-assign
      </button>

      <!-- History Dropdown -->
      <div class="dropdown">
        <!-- Display number of comments in button -->
        <button @click="toggleDropdown" class="btn dropdown-toggle">Comments <span v-if="commentsCount > 0"
            class="comments-count">{{ commentsCount }}</span> & History ▼</button>
        <div v-if="dropdownOpen" class="dropdown-menu">
          <!-- Comment field and history filter  -->
          <div class="history-controls">
            <div class="invoice-comment-section">
              <span class="detail-label"> Invoice Comment </span>
              <textarea class="comment-input" v-model="localInvoice.comment" @input="handleCommentChange" rows="2"
                placeholder="Add a comment..." maxlength="3000"></textarea>
              <span class="meta">Characters: {{ localInvoice.comment ? localInvoice.comment.length : 0 }}/3000</span>
            </div>
            <label for="history-filter" class="sr-only">Show</label>
            <select id="history-filter" v-model="historyFilter" class="attachment-selector">
              <option value="comments">Comments only</option>
              <option value="all">All history</option>
            </select>
          </div>

          <div v-if="filteredHistory?.length">
            <InvoiceHistoryLine v-for="line in filteredHistory" :key="line.timestamp + line.username" :line="line" />
          </div>
          <p v-else class="no-rows">No history available.</p>
        </div>
      </div>


    </div>
    <!-- Main layout wrapper -->
    <div class="invoice-layout">
      <!-- Left side content (or full width on mobile) -->
      <div class="invoice-content">
        <div class="invoice-details">
          <div class="invoice-section">
            <details open>
              <summary>
                <div class="content-header">
                  <div class="content-header-title">
                    <h3>Invoice Details</h3>
                  </div>
                  <div class="content-header-badge">
                    Approval status: {{ localInvoice.approvalStatusTitle }}
                  </div>
                </div>
              </summary>
              <dl class="invoice-details-list">
                <div v-for="(label, index) in primaryDetails" :key="index" class="invoice-detail-item">
                  <dt class="detail-label">{{ label.name }}</dt>
                  <dd class="detail-value">{{ label.value || '-' }}</dd>
                </div>
              </dl>
            </details>
            <!-- Display invoice description if available-->
            <div class="invoice-description">
              <span class="detail-label">Note</span>
              <p class="detail-value">{{ localInvoice.invoiceDescription || '-' }}</p>
            </div>
          </div>

          <!-- Additional invoice header data -->
          <div class="invoice-section">
            <details>
              <summary>
                <div class="content-header">
                  <div class="content-header-title">
                    <h3>More Details</h3>
                  </div>
                  <div class="content-header-badge">
                    Invoice status: {{ localInvoice.invoiceStatus }}
                  </div>
                </div>

                <dl class="invoice-details-list">
                  <div class="invoice-detail-item">
                    <dt class="detail-label">Our reference </dt>
                    <dd class="detail-value">{{ localInvoice.ourReference || '-' }}</dd>
                  </div>
                  <div class="invoice-detail-item">
                    <dt class="detail-label">Vendor Bank Account </dt>
                    <dd class="detail-value">{{ localInvoice.vendorBankAccountNumber }}</dd>
                  </div>
                  <div class="invoice-detail-item">
                    <dt class="detail-label">Invoice Id </dt>
                    <dd class="detail-value">{{ localInvoice.externalId }}</dd>
                  </div>
                </dl>
                <br>
              </summary>

              <dl class="invoice-details-list">
                <div v-for="(label, index) in invoiceDetails" :key="index" class="invoice-detail-item">
                  <dt class="detail-label">{{ label.name }}</dt>
                  <dd class="detail-value">{{ label.value || '-' }}</dd>
                </div>
              </dl>
            </details>
          </div>

          <!-- Accounting rows -->
          <div class="invoice-section">
            <div class="content-header">
              <div class="content-header-title">
                <h3>Accounting Rows</h3>
              </div>
              <div class="content-header-badge">
                Accounted: {{ localInvoice.accounted ? 'Yes' : 'No' }}
              </div>
            </div>
            <div class="accounting-actions">
              <!-- Dropdown action: Copy from first row -->
              <div class="action-dropdown">
                <button class="btn btn-dropdown" @click="toggleCopyDropdown" type="button">
                  Copy from first row ▼
                </button>
                <ul id="action-menu" class="action-dropdown-menu" v-if="copyDropdownOpen">
                  <li><button @click="copyVatCodes" class="dropdown-item">VAT codes</button></li>
                  <li><button @click="copyVatPercentages" class="dropdown-item">VAT percentages</button></li>
                  <li><button @click="copyAccountNumbers" class="dropdown-item">Account numbers</button></li>
                  <li><button @click="copyDimensions" class="dropdown-item">Dimensions</button></li>
                </ul>
              </div>

              <!-- Post Single Entry. Should merge all accounting rows into one -->
              <!--  <button @click="mergeAccountingRows" class="btn btn-action">Post Single Entry</button> -->

              <!-- Add row and save changes buttons -->
              <button @click="addRow" class="btn btn-add">Add Accounting Row</button>
              <!-- Hidden save changes button  -->
              <!--
              <button @click="saveChanges" class="btn btn-save" :disabled="saving">
                Save changes
              </button>
              -->
            </div>
            <div class="accounting-table-container">
              <table class="accounting-table" v-if="localInvoice.accountingRows?.length > 0">
                <colgroup>
                  <col style="width: 200px;"><!-- Description -->
                  <col style="width: 80px;"><!-- Amount -->
                  <col style="width: 100px;"><!-- Vat Code -->
                  <col style="width: 80px;"><!-- Vat Percent -->
                  <col style="width: 120px;"><!-- Account -->
                  <col v-for="dimension in dimensions" :key="dimension.id" style="width: 120px;">
                  <!-- <col style="width: 80px;"> --><!-- Actions -->
                </colgroup>
                <thead>
                  <tr>
                    <th>Product name</th>
                    <th>Amount</th>
                    <th>Vat code</th>
                    <th>Vat %</th>
                    <th>Account</th>
                    <th v-for="dimension in dimensions" :key="dimension.id" :title="dimension.name">
                      {{ dimension.name }}
                    </th>
                    <!--<th>Actions</th>-->
                  </tr>
                </thead>
                <tbody>
                  <InvoiceAccountingRow v-for="row in localInvoice.accountingRows" :key="row.id" :row="row"
                    :accounts="accounts" :dimensions="dimensions" :vatCodes="vatCodes" @updateRow="handleRowUpdate"
                    @removeRow="handleRowRemove" />
                </tbody>
              </table>
              <p v-else class="no-rows">No accounting rows added yet.</p>
            </div>
            <div class="accounting-summary">
              <div class="total">
                <strong>Total Accounting Amount:</strong> {{ formatCurrency(totalAccountingAmount) }}
              </div>
              <div class="balance" :class="{ 'balance-warning': hasBalanceWarning }">
                <strong>Balance:</strong> {{ formatCurrency(balance) }}
                <span v-if="hasBalanceWarning" class="warning-text">
                  Accounting total doesn't match invoice amount
                </span>
              </div>
            </div>
          </div>
        </div>
        <!-- End of invoice content-->
      </div>

      <!-- Right side attachment viewer (or bottom on mobile) -->
      <div class="invoice-image-container" v-if="selectedAttachment">
        <div class="invoice-image-header">
          <div class="header-actions">
            <!-- Attachment selection -->
            <select v-model="selectedAttachmentIndex" class="attachment-selector">
              <option v-for="(attachment, index) in localInvoice.attachments" :key="attachment.fileName" :value="index">
                {{ attachment.fileName }}
              </option>
            </select>
            <i> {{ selectedAttachment.comment }}</i>
            <a :href="getDownloadUrl(selectedAttachment)" :download="selectedAttachment.fileName" class="download-link">
              Download ⬇️
            </a>
          </div>
        </div>

        <!-- Image display -->
        <div class="invoice-image-wrapper" :class="{ 'expanded': isImageExpanded }"
          v-if="isImage(selectedAttachment.contentType)">
          <img :src="getDataUrl(selectedAttachment)" :alt="selectedAttachment.fileName" class="invoice-image"
            @click="toggleImageSize" />
        </div>

        <!-- PDF display -->
        <div class="pdf-viewer-wrapper" :class="{ 'expanded': isImageExpanded }"
          v-else-if="isPdf(selectedAttachment.contentType)">
          <iframe :src="getPdfUrl(selectedAttachment)" class="pdf-viewer"></iframe>
        </div>

        <!-- Unsupported file type -->
        <div class="unsupported-file" v-else>
          <div class="file-icon">📄</div>
          <p>{{ selectedAttachment.fileName }}</p>
          <p class="file-info">{{ selectedAttachment.contentType }}</p>
          <p class="file-comment" v-if="selectedAttachment.comment">{{ selectedAttachment.comment }}</p>
        </div>
      </div>
    </div>
  </div>

</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from 'vue';
import { Account, AccountingRow, Dimension, PurchaseInvoice, VatCode } from '@/types/invoice';
import InvoiceHistoryLine from './InvoiceHistoryLine.vue';
import InvoiceAccountingRow from "@/components/AccountingRow.vue";
import { v4 as uuidv4 } from 'uuid';

export default defineComponent({
  name: 'InvoiceDetails',
  components: { InvoiceAccountingRow, InvoiceHistoryLine },
  props: {
    invoice: {
      type: Object as PropType<PurchaseInvoice>,
      required: true
    },
    accounts: {
      type: Array as PropType<Account[]>,
      required: true
    },
    dimensions: {
      type: Array as PropType<Dimension[]>,
      required: true
    },
    vatCodes: {
      type: Array as PropType<VatCode[]>,
      required: true
    },
    invoiceImageBase64: {
      type: String,
      required: false
    }
  },
  emits: ['approve', 'reject', 'addRow', 'update', 'updateInvoice'],
  setup(props, { emit }) {
    const saving = ref(false);
    const isImageExpanded = ref(false);
    const selectedAttachmentIndex = ref(0);
    const dropdownOpen = ref(false);
    const historyFilter = ref('comments'); // default to comments only
    // Dropdown for accounting actions
    const copyDropdownOpen = ref(false);

    // Create a local copy of the invoice to track changes
    const localInvoice = ref<PurchaseInvoice>({ ...props.invoice });

    const handleCommentChange = () => {
      emit('updateInvoice', localInvoice.value);
    };

    const toggleDropdown = () => {
      dropdownOpen.value = !dropdownOpen.value;
    };

    const toggleCopyDropdown = () => {
      copyDropdownOpen.value = !copyDropdownOpen.value;
    };
    // Sync local invoice when the prop changes
    watch(() => props.invoice, (newInvoice) => {
      if (newInvoice) {
        localInvoice.value = { ...newInvoice };
      }
    }, { deep: true });

    // Computed properties

    const primaryDetails = computed((): { name: string; value: string; }[] => {
      return [
        { name: 'Customer Name', value: localInvoice.value?.customerName },
        { name: 'Invoice Number', value: localInvoice.value?.invoiceNumber },
        { name: 'Invoice Date', value: formatDate(localInvoice.value.invoiceDate) },
        { name: 'Event Date', value: formatDate(localInvoice.value.eventDate) },
        { name: 'Due Date', value: formatDate(localInvoice.value.dueDate) },
        { name: 'Reference Number', value: localInvoice.value.referenceNumber },
        { name: 'Invoice Amount', value: formatCurrency(localInvoice.value.invoiceAmount) },
        { name: 'Vendor Name', value: localInvoice.value.vendorName },
      ];
    });

    const invoiceDetails = computed((): { name: string; value: string; }[] => {
      return [
        { name: 'Invoice ID', value: localInvoice.value.externalId },
        { name: 'Foreign Currency Amount', value: formatCurrency(localInvoice.value.foreignCurrencyAmount, localInvoice.value.foreignCurrencyNameID) },
        { name: 'Your Reference', value: localInvoice.value.yourReference }
      ];
    });

    const invoiceTotal = computed((): number => {
      return localInvoice.value?.invoiceLines?.reduce((sum, row) => sum + row.lineSum, 0) || 0;
    });

    const totalAccountingAmount = computed((): number => {
      return localInvoice.value?.accountingRows?.reduce((sum, row) => sum + row.amount, 0) || 0;
    });

    const balance = computed((): number => {
      return invoiceTotal.value - totalAccountingAmount.value;
    });

    const hasBalanceWarning = computed((): boolean => {
      return Math.abs(balance.value) > 0.01; // Allow small rounding differences
    });

    const selectedAttachment = computed(() => {
      return localInvoice.value?.attachments?.[selectedAttachmentIndex.value] || null;
    });

    const commentsCount = computed(() => {
      return localInvoice.value?.handlingHistory?.filter(h => h?.type.toLowerCase() === 'comment').length || 0;
    });

    // File type checking methods
    const isImage = (contentType: string): boolean => {
      return contentType.startsWith('image/');
    };

    const isPdf = (contentType: string): boolean => {
      return contentType === 'application/pdf';
    };

    const isViewableInline = (contentType: string): boolean => {
      return isImage(contentType) || isPdf(contentType);
    };

    // Data URL generation methods
    const getDataUrl = (attachment: any): string => {
      return `data:${attachment.contentType};base64,${attachment.attachmentBase64Data}`;
    };

    const getPdfUrl = (attachment: any): string => {
      return `data:application/pdf;base64,${attachment.attachmentBase64Data}`;
    };

    const getDownloadUrl = (attachment: any): string => {
      return `data:${attachment.contentType};base64,${attachment.attachmentBase64Data}`;
    };

    const getFileTypeLabel = (contentType: string): string => {
      if (isImage(contentType)) return 'IMAGE';
      if (isPdf(contentType)) return 'PDF';
      if (contentType.includes('word')) return 'WORD';
      if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'EXCEL';
      if (contentType.includes('text')) return 'TEXT';
      return 'FILE';
    };

    const toggleImageSize = () => {
      isImageExpanded.value = !isImageExpanded.value;
    };

    const filteredHistory = computed(() => {
      const history = localInvoice.value?.handlingHistory || [];
      if (historyFilter.value === 'all') return history;
      // Default: return only comments
      return history.filter(h => {
        if (!h) return false;
        return h.type.toLowerCase() === 'comment';
      });
    });
    // Methods
    const addRow = () => {
      const newRow: AccountingRow = {
        id: uuidv4(),
        accountId: '',
        amount: 0,
        productName: `inv: ${localInvoice.value.invoiceNumber}`,
        vatCode: '',
        vatPercent: '0',
        deliveredAmount: 0,
        unitPrice: 0,
        discountPercentage: 0
      };

      if (!localInvoice.value.accountingRows) {
        localInvoice.value.accountingRows = [];
      }

      localInvoice.value.accountingRows.push(newRow);
      emit('updateInvoice', JSON.parse(JSON.stringify(localInvoice.value)));
    };

    const handleRowUpdate = (updatedRow: AccountingRow) => {
      const index = localInvoice.value.accountingRows.findIndex(row => row.id === updatedRow.id);
      if (index !== -1) {
        localInvoice.value.accountingRows[index] = updatedRow;
        emit('updateInvoice', localInvoice.value);
      }
    };

    const handleRowRemove = (rowId: string) => {
      localInvoice.value.accountingRows = localInvoice.value.accountingRows.filter(row => row.id !== rowId);
      emit('updateInvoice', localInvoice.value);
    };

    const saveChanges = () => {
      emit('update', localInvoice.value);
    };

    const approve = () => {
      emit('approve', localInvoice.value);
    };

    const approveAndNext = () => {
      localInvoice.value.autoFetchNextInvoice = true;
      const updated = JSON.parse(JSON.stringify(localInvoice.value));
      emit('updateInvoice', updated);
      emit('approve');
    };

    const reject = () => {
      emit('reject', localInvoice.value);
    };

    // Post Single Entry
    const mergeAccountingRows = () => {
      if (!localInvoice.value.accountingRows?.length) return;

      const total = localInvoice.value.accountingRows.reduce((sum, row) => sum + row.amount, 0);
      const firstRow = { ...localInvoice.value.accountingRows[0], amount: total };

      localInvoice.value.accountingRows = [firstRow];
      emit('updateInvoice', localInvoice.value);
    };

    /**
     * Copies a field value from the first accounting row to all other rows.
     * @param field - The field name to copy
     * @throws {Error} If there are no accounting rows to copy from.
     */
    const copyFieldToRows = (field: keyof AccountingRow): void => {
      if (!localInvoice.value.accountingRows || localInvoice.value.accountingRows.length === 0) {
        throw new Error(`No accounting rows to copy ${String(field)} from.`);
      }
      const firstValue = localInvoice.value.accountingRows[0][field];
      localInvoice.value.accountingRows = localInvoice.value.accountingRows.map((row, idx) =>
        idx === 0 ? row : { ...row, [field]: firstValue }
      );
      emit('updateInvoice', localInvoice.value);
      toggleCopyDropdown();
    };

    /**
     * Copies the VAT code from the first accounting row to all other rows.
     * @throws {Error} If there are no accounting rows to copy from.
     */
    const copyVatCodes = (): void => {
      copyFieldToRows('vatCode');
    };

    /**
     * Copies the VAT percentage from the first accounting row to all other rows.
     * @throws {Error} If there are no accounting rows to copy from.
     */
    const copyVatPercentages = (): void => {
      copyFieldToRows('vatPercent');
    };

    /**
     * Copies the account number from the first accounting row to all other rows.
     * @throws {Error} If there are no accounting rows to copy from.
     */
    const copyAccountNumbers = (): void => {
      copyFieldToRows('accountId');
    };

    /**
     * Copies the dimension values from the first accounting row to all other rows.
     * Separately handled due to array nature.
     * @throws {Error} If there are no accounting rows to copy from.
     */
    const copyDimensions = (): void => {
      if (!localInvoice.value.accountingRows || localInvoice.value.accountingRows.length === 0) {
        throw new Error('No accounting rows to copy dimensions from.');
      }
      const firstRow = localInvoice.value.accountingRows[0];
      localInvoice.value.accountingRows = localInvoice.value.accountingRows.map((row, idx) =>
        idx === 0 ? row : { ...row, dimensions: firstRow.dimensions ? [...firstRow.dimensions] : undefined }
      );
      emit('updateInvoice', localInvoice.value);
      toggleCopyDropdown();
    };

    const formatCurrency = (value: number, currency?: string): string => {
      if (value === undefined) return '0,00€';
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: currency || 'EUR'
      }).format(value);
    };

    const formatDate = (dateString?: string): string => {
      if (!dateString) return '-';
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day));
      return date.toLocaleDateString();
    };

    return {
      localInvoice,
      primaryDetails,
      invoiceDetails,
      saving,
      invoiceTotal,
      totalAccountingAmount,
      balance,
      hasBalanceWarning,
      formatDate,
      selectedAttachment,
      selectedAttachmentIndex,
      addRow,
      saveChanges,
      handleRowUpdate,
      handleRowRemove,
      isImage,
      isPdf,
      isViewableInline,
      mergeAccountingRows,
      approve,
      approveAndNext,
      reject,
      handleCommentChange,
      formatCurrency,
      getDataUrl,
      getPdfUrl,
      getDownloadUrl,
      getFileTypeLabel,
      isImageExpanded,
      toggleImageSize,
      dropdownOpen,
      historyFilter,
      filteredHistory,
      toggleDropdown,
      copyVatCodes,
      copyVatPercentages,
      copyAccountNumbers,
      copyDimensions,
      copyDropdownOpen,
      toggleCopyDropdown,
      commentsCount
    };
  }
});
</script>

<style scoped>
.invoice-container {
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding-top: 50px;
  /* Space for fixed actions bar */
}

.invoice-actions {
  height: 50px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: white;
  padding: 8px;
  display: flex;
  gap: 8px;
  align-items: center;
  border-bottom: 1px solid #eee;
}

.invoice-layout {
  flex: 1;
  display: grid;
  gap: 8px;
  height: calc(100vh - 50px);
  overflow: hidden;
}

/* Desktop layout: side by side */
@media (min-width: 1024px) {
  .invoice-layout {
    grid-template-columns: 3fr 2fr;
    grid-template-areas: "content image";
  }

  .invoice-content {
    grid-area: content;
    overflow-y: auto;
    padding: 12px;
  }

  .invoice-image-container {
    grid-area: image;
    overflow-y: auto;
    height: 100%;
    padding: 12px;
    margin-top: 12px;
  }
}

/* Mobile layout: stacked */
@media (max-width: 1023px) {
  .invoice-layout {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr;
    grid-template-areas:
      "content"
      "image";
  }

  .invoice-content {
    grid-area: content;
    overflow-y: auto;
    padding: 10px;
  }

  .invoice-image-container {
    grid-area: image;
    overflow-y: auto;
    min-width: unset;
    padding: 10px;
  }
}

details summary::after {
  content: "\203A";
  font-style: italic;
  font-size: 1.5rem;
  color: gray;
}

details[open] summary::after {
  content: " ";
}

summary {
  cursor: pointer;
  outline: none;
}

/* Remove default disclosure icon in WebKit browsers (Chrome, Safari) */
details>summary {
  list-style: none;
  -webkit-list-style: none;
  appearance: none;
}

/* For Firefox, hide the marker with a pseudo-element or override */
details>summary::-webkit-details-marker {
  display: none;
}

details>summary::marker {
  display: none;
}

.invoice-details {
  padding: 0px;
  max-width: 1200px;
  margin: 0 auto;
}

.invoice-section {
  background-color: white;
  margin-bottom: 16px;
  border: 1px solid #eee;
  border-radius: 5px;
  padding: 16px;
}


.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
}

.content-header-title h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 600;
}

.content-header-badge {
  padding: 0.4em 0.8em;
  background-color: var(--green-light-10);
  color: var(--green-base);
  border-radius: 1rem;
  font-size: 0.875rem;
  font-weight: bold;
  white-space: nowrap;
  text-transform: capitalize;
}

.invoice-details-list {
  display: grid;
  gap: 16px;
  width: 100%;
}

.invoice-detail-item {
  display: flex;
  flex-direction: column;
}

.detail-label {
  font-weight: bold;
  font-size: var(--font-size-sm);
  color: #555;
  margin: 0;
}

.detail-value {
  margin: 0;
  font-size: var(--font-size-normal);
  color: #333;
  word-break: break-word;
}

/* Responsive breakpoints for invoice details */
@media (max-width: 768px) {
  .invoice-details-list {
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .invoice-details-list {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1025px) {
  .invoice-details-list {
    grid-template-columns: repeat(3, 1fr);
  }
}

.accounting-actions {
  margin-bottom: 10px;
  display: flex;
  justify-content: flex-end;
}

.accounting-table-container {
  overflow-x: auto;
  max-height: 40vh;
}

.accounting-table {
  table-layout: fixed;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 8px;
  margin-top: 10px;
  margin-bottom: 20px;
  background-color: #fff;
  font-size: var(--font-size-sm);
}

.accounting-table th,
.accounting-table td {
  border-right: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  padding: 8px;
  text-align: left;
  vertical-align: middle;
}

.accounting-table th {
  background-color: #BDBDBD1A;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Remove borders on last column and last row */
.accounting-table th:last-child,
.accounting-table td:last-child {
  border-right: none;
}

.accounting-table tbody tr:last-child td {
  border-bottom: none;
}

/* Remove top border from cells since table has border */
.accounting-table th {
  border-top: none;
}

.comment-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
  font-size: var(--font-size-sm);
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: vertical;
}

.accounting-table input[type="number"],
.accounting-table select {
  width: 100%;
  padding: 4px 6px;
  font-size: var(--font-size-sm);
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.accounting-summary {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.balance-warning {
  color: var(--red-base);
}

.warning-text {
  font-size: 0.85em;
  margin-left: 10px;
}

.file-type-badge {
  background-color: #f0f0f0;
  color: #666;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75em;
  font-weight: bold;
  text-transform: uppercase;
}

.pdf-viewer-wrapper {
  height: calc(100vh - 200px);
  max-height: unset;
}

.pdf-viewer {
  width: 100%;
  height: 100%;
  border: none;
}

/* Invoice image styles */
.invoice-image-container {
  width: 100%;
  box-sizing: border-box;
  background-color: white;
  border: 1px solid #eee;
  border-radius: 5px;
  padding: 15px;
  min-width: 600px;
}

.invoice-image-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.attachment-selector {
  padding: 6px 12px;
  border: 1px solid #ccc;
  border-radius: var(--btn-default-radius);
  font-size: var(--font-size-sm);
  cursor: pointer;
}

.invoice-image-header h3 {
  margin: 0;
}

.invoice-image-wrapper {
  width: 100%;
  max-height: 800px;
  overflow: auto;
  position: relative;
  overflow: hidden;
  border-radius: 5px;
  border: 1px solid #ddd;
}

.invoice-image {
  width: 100%;
  max-width: 100%;
  height: auto;
  display: block;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.invoice-image:hover {
  transform: scale(1.02);
}

/* Expanded image state */
.invoice-image-wrapper.expanded {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2000;
  width: 90vw;
  height: 90vh;
  background-color: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.invoice-image-wrapper.expanded .invoice-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: zoom-out;
}

/* Overlay for expanded image */
.invoice-image-wrapper.expanded::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: -1;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: var(--btn-default-radius);
  cursor: pointer;
  font-weight: bolder;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-add {
  background-color: var(--green-base);
  color: white;
}

.btn-save {
  background-color: white;
  border: 1px solid gray;
}

.btn-approve {
  background-color: var(--green-base);
  color: white;
}

.btn-reject {
  background-color: var(--red-base);
  color: white;
}

.download-link {
  background-color: var(--blue-base);
  min-width: fit-content;
  color: white;
  padding: 6px 12px;
  margin-left: 4px;
  border: none;
  border-radius: var(--btn-default-radius);
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: bolder;
  text-decoration: none;
}

.no-rows {
  font-style: italic;
  color: #666;
}

.total,
.balance {
  text-align: right;
  margin-top: 5px;
}

.action-dropdown {
  position: relative;
  display: inline-block;
  margin-right: 10px;
}

.action-dropdown-menu {
  position: absolute;
  right: 0;
  background-color: white;
  min-width: 160px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
  border: 1px solid #ccc;
  border-radius: var(--btn-default-radius);
  z-index: 1000;
  margin-top: 4px;
  padding: 0;
  list-style-type: none;
}

.action-dropdown-menu li button {
  display: block;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;

}

.dropdown {
  position: absolute;
  right: 60px;
  display: inline-block;
  margin-left: 10px;
}

.dropdown-toggle {
  background-color: white;
  border: 1px solid #ccc;
  padding: 6px 12px;
  cursor: pointer;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 240px;
  max-height: 90vh;
  overflow-y: auto;
  scrollbar-gutter: stable;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: var(--btn-default-radius);
  z-index: 1000;
  padding: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

.dropdown-item {
  padding: 10px;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
}

.dropdown-item:hover {
  background-color: #c9c7c7;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item b {
  font-size: 15px;
  color: #222;
}

.field-changes {
  margin-top: 8px;
  padding-left: 16px;
}

.field-changes li {
  margin-bottom: 6px;
  list-style: none;
}

.field-change {
  color: #555;
  font-style: italic;
}

.meta {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #888;
}

.comments-count {
  color: white;
  background-color: var(--red-base);
  font-weight: 600;
  border: 1px solid var(--red-base);
  border-radius: 12px;
  padding: 2px 6px;
}
</style>